<script>
    import { onMount } from "svelte";
    import { push } from "svelte-spa-router";
    import { db } from "../db/index.js";
    import { progress as progressStore } from "../stores.js";
    import { sanitizeHtml } from "@edu-quizz/shared/sanitize";
    import { RESOURCE_TYPES } from "@edu-quizz/shared/constants/resourceTypes";
    import SingleChoiceOptions from "@edu-quizz/shared/components/quiz/SingleChoiceOptions.svelte";
    import MultipleChoiceOptions from "@edu-quizz/shared/components/quiz/MultipleChoiceOptions.svelte";
    import MatchOptions from "@edu-quizz/shared/components/quiz/MatchOptions.svelte";
    import HotspotOptions from "@edu-quizz/shared/components/quiz/HotspotOptions.svelte";

    export let params = {};

    let quiz = null;
    let questions = [];
    let loading = true;
    let error = null;

    // Quiz state
    let currentQuestionIndex = 0;
    let userAnswers = []; // Array of selected option IDs for each question
    let showResults = false;
    let score = 0;
    let hideCorrectAnswers = false;

    $: currentQuestion = questions[currentQuestionIndex];
    $: hasNext = currentQuestionIndex < questions.length - 1;
    $: hasPrev = currentQuestionIndex > 0;
    $: progress =
        questions.length > 0
            ? ((currentQuestionIndex + 1) / questions.length) * 100
            : 0;
    $: isAnswered = currentQuestion?.questionType === 'match'
        ? Object.keys(userAnswers[currentQuestionIndex] || {}).length > 0
        : userAnswers[currentQuestionIndex]?.length > 0;

    function isAnswerEmpty(question, answer) {
        if (question.questionType === 'match') return !answer || Object.keys(answer).length === 0;
        return !answer || answer.length === 0;
    }

    async function loadQuiz() {
        loading = true;
        error = null;

        try {
            const quizId = parseInt(params.id);

            // Get the quiz with its questions and options from the database
            const quizData = await db.getQuizWithQuestions(quizId);

            if (!quizData) {
                error = "Quiz non trouvé";
                loading = false;
                return;
            }

            quiz = quizData;
            questions = quizData.questions || [];

            // Initialize user answers array (empty arrays for choice questions, empty objects for match questions)
            userAnswers = questions.map(q => q.questionType === 'match' ? {} : []);

            // Load existing answers from the database
            await loadExistingAnswers(quizId);

            loading = false;
        } catch (err) {
            console.error("Error loading quiz:", err);
            error = err.message;
            loading = false;
        }
    }

    async function loadExistingAnswers(quizId) {
        try {
            // Get all saved answers for this quiz
            const savedAnswers = await db.quizAnswers
                .where("quizResourceServerId")
                .equals(quizId)
                .toArray();

            if (savedAnswers.length === 0) {
                return;
            }

            // Map saved answers to questions
            questions.forEach((question, index) => {
                const savedAnswer = savedAnswers.find(
                    (answer) => answer.questionServerId === question.serverId
                );

                if (savedAnswer && savedAnswer.selectedOptionIds) {
                    userAnswers[index] = savedAnswer.selectedOptionIds;
                }
            });

            // Trigger reactivity
            userAnswers = userAnswers;

            console.log("Loaded existing answers:", savedAnswers.length);
        } catch (err) {
            console.error("Error loading existing answers:", err);
        }
    }

    async function handleAnswer(optionId, matchId = null) {
        const question = currentQuestion;

        if (question.questionType === "single_choice") {
            // Single choice: replace with new selection
            userAnswers[currentQuestionIndex] = [optionId];
        } else if (question.questionType === "multiple_choice" || question.questionType === "image_hotspot") {
            // Multiple choice / hotspot: toggle selection
            const currentAnswers = [...userAnswers[currentQuestionIndex]];
            const index = currentAnswers.indexOf(optionId);
            if (index > -1) {
                currentAnswers.splice(index, 1);
            } else {
                currentAnswers.push(optionId);
            }
            userAnswers[currentQuestionIndex] = currentAnswers;
        } else if (question.questionType === "match") {
            // Match: set or clear the match
            const currentMatches = { ...userAnswers[currentQuestionIndex] };
            if (matchId === null) {
                delete currentMatches[optionId];
            } else {
                currentMatches[optionId] = matchId;
            }
            userAnswers[currentQuestionIndex] = currentMatches;
        }

        // Trigger reactivity
        userAnswers = userAnswers;

        // Auto-save the answer
        await saveCurrentAnswer();
    }

    async function saveCurrentAnswer() {
        try {
            const quizId = parseInt(params.id);
            const question = currentQuestion;
            const answer = userAnswers[currentQuestionIndex];

            // Check if answer is empty
            const isEmpty = question.questionType === 'match'
                ? !answer || Object.keys(answer).length === 0
                : !answer || answer.length === 0;

            if (isEmpty) {
                return;
            }

            // Calculate if answer is correct
            let isCorrect;
            if (question.questionType === 'match') {
                // For match: each option should be matched to itself (optionId === matchId)
                isCorrect = question.options.every(opt =>
                    answer[opt.serverId] === opt.serverId
                );
            } else {
                const correctOptions = question.options
                    .filter((opt) => opt.isCorrect)
                    .map((opt) => opt.serverId);
                isCorrect =
                    answer.length === correctOptions.length &&
                    answer.every((id) => correctOptions.includes(id));
            }

            // Check if answer already exists
            const existingAnswer = await db.quizAnswers
                .where("quizResourceServerId")
                .equals(quizId)
                .and((item) => item.questionServerId === question.serverId)
                .first();

            const answerData = {
                quizResourceServerId: quizId,
                questionServerId: question.serverId,
                selectedOptionIds: answer,
                answerText: null,
                isCorrect,
                answeredAt: new Date().toISOString(),
            };

            if (existingAnswer) {
                // Update existing answer
                answerData.uuid = existingAnswer.uuid;
                await db.quizAnswers.put(answerData);

                // Add to sync queue (always use "create" for batch sync)
                await db.addToSyncQueue(
                    "create",
                    RESOURCE_TYPES.QUIZ,
                    quizId,
                    answerData,
                    "quizAnswers",
                    existingAnswer.uuid
                );
            } else {
                // Create new answer
                const uuid = crypto.randomUUID();
                answerData.uuid = uuid;
                await db.quizAnswers.put(answerData);

                // Add to sync queue
                await db.addToSyncQueue(
                    "create",
                    RESOURCE_TYPES.QUIZ,
                    quizId,
                    answerData,
                    "quizAnswers",
                    uuid
                );
            }
        } catch (err) {
            console.error("Error saving current answer:", err);
        }
    }

    function nextQuestion() {
        if (hasNext) {
            currentQuestionIndex++;
        }
    }

    function prevQuestion() {
        if (hasPrev) {
            currentQuestionIndex--;
        }
    }

    function calculateScore() {
        let correctCount = 0;

        questions.forEach((question, index) => {
            const userAnswer = userAnswers[index];

            let isCorrect;
            if (question.questionType === 'match') {
                // For match: each option should be matched to itself (optionId === matchId)
                isCorrect = question.options.every(opt =>
                    userAnswer[opt.serverId] === opt.serverId
                );
            } else {
                const correctOptions = question.options
                    .filter((opt) => opt.isCorrect)
                    .map((opt) => opt.serverId);
                isCorrect =
                    userAnswer.length === correctOptions.length &&
                    userAnswer.every((id) => correctOptions.includes(id));
            }

            if (isCorrect) {
                correctCount++;
            }
        });

        return correctCount;
    }

    async function submitQuiz() {
        // Check if all questions are answered
        const allAnswered = userAnswers.every((answer, index) => {
            const question = questions[index];
            if (question.questionType === 'match') {
                return answer && Object.keys(answer).length === question.options.length;
            }
            return answer && answer.length > 0;
        });

        if (!allAnswered) {
            if (
                !confirm(
                    "Vous n'avez pas répondu à toutes les questions. Voulez-vous soumettre quand même ?",
                )
            ) {
                return;
            }
        }

        score = calculateScore();
        showResults = true;

        // Save progress to database
        try {
            const quizId = parseInt(params.id);
            const percentage = (score / questions.length) * 100;

            await progressStore.save(quizId, RESOURCE_TYPES.QUIZ, true, percentage);

            // Note: All answers are already saved automatically as the user progresses
            // No need to re-save them here to avoid duplicate sync queue entries
        } catch (err) {
            console.error("Error saving quiz progress:", err);
        }
    }

    async function restartQuiz() {
        currentQuestionIndex = 0;
        userAnswers = questions.map(q => q.questionType === 'match' ? {} : []);
        showResults = false;
        score = 0;

        // Reload any existing answers (in case user wants to resume)
        const quizId = parseInt(params.id);
        await loadExistingAnswers(quizId);
    }

    async function resetQuizAnswers() {
        if (
            !confirm(
                "Voulez-vous vraiment réinitialiser toutes les réponses de ce quiz ? Vous pourrez recommencer le quiz depuis le début.",
            )
        ) {
            return;
        }

        try {
            const quizId = parseInt(params.id);

            // Delete all answers for this quiz from local database
            await db.quizAnswers
                .where("quizResourceServerId")
                .equals(quizId)
                .delete();

            console.log(`Deleted local answers for quiz ${quizId}`);

            // Add reset action to sync queue
            await db.addToSyncQueue(
                "reset_quiz",
                RESOURCE_TYPES.QUIZ,
                quizId,
                {},
                "quizAnswers", // Target local table name
                null,
            );

            // Restart the quiz
            restartQuiz();
        } catch (err) {
            console.error("Error resetting quiz answers:", err);
            error = "Erreur lors de la réinitialisation du quiz";
        }
    }

    function goBack() {
        push("/");
    }

    function isQuestionCorrect(questionIndex) {
        const question = questions[questionIndex];
        const userAnswer = userAnswers[questionIndex];

        if (question.questionType === 'match') {
            // For match: each option should be matched to itself (optionId === matchId)
            return question.options.every(opt =>
                userAnswer[opt.serverId] === opt.serverId
            );
        }

        const correctOptions = question.options
            .filter((opt) => opt.isCorrect)
            .map((opt) => opt.serverId);

        return (
            userAnswer.length === correctOptions.length &&
            userAnswer.every((id) => correctOptions.includes(id))
        );
    }

    onMount(() => {
        loadQuiz();
    });
</script>

<div class="quiz-page">
    <header class="page-header">
        <button class="back-btn btn btn-outline btn-content" on:click={goBack}>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
            >
                <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Retour
        </button>
        {#if quiz}
            <h1 class="text-base-content">{quiz.name}</h1>
        {/if}
    </header>

    {#if loading}
        <div class="loading">
            <div class="spinner loading-spinner loading-lg border-primary border-t-transparent"></div>
            <p>Chargement du quiz...</p>
        </div>
    {:else if error}
        <div class="error text-base-content/70">
            <p>Erreur: {error}</p>
            <button class="btn btn-primary" on:click={loadQuiz}>Réessayer</button>
        </div>
    {:else if questions.length === 0}
        <div class="empty text-base-content/70">
            <p>Aucune question dans ce quiz.</p>
            <button class="secondary-btn btn btn-outline btn-primary" on:click={goBack}>Retour</button>
        </div>
    {:else if showResults}
        <div class="results">
            <h2 class="text-content">Quiz terminé!</h2>
            <div class="score-display">
                <div class="score-circle bg-gradient-to-br from-primary to-secondary text-primary-content">
                    <span class="score-number"
                        >{score}/{questions.length}</span
                    >
                    <span class="score-label"
                        >{Math.round((score / questions.length) * 100)}%</span
                    >
                </div>
            </div>

            <div class="results-list">
                <div class="results-list-header">
                    <h3 class="text-content">Révision des réponses</h3>
                    <label class="hide-correct-toggle">
                        <input
                            type="checkbox"
                            class="checkbox checkbox-primary"
                            bind:checked={hideCorrectAnswers}
                        />
                        <span class="text-base-content">Masquer les réponses correctes</span>
                    </label>
                </div>
                {#each questions as question, index}
                    {#if !hideCorrectAnswers || !isQuestionCorrect(index)}
                    {@const isCorrect = isQuestionCorrect(index)}
                    {@const correctOptions = question.options.filter(
                        (opt) => opt.isCorrect,
                    )}
                    {@const userAnswer = userAnswers[index]}

                    <div class="result-item bg-base-200 {isCorrect ? 'border-success' : 'border-error'}" class:correct={isCorrect}>
                        <div class="result-header">
                            <span class="question-number text-base-content/70"
                                >Question {index + 1}</span
                            >
                            <span class="result-badge {isCorrect ? 'bg-success/10 text-success' : 'bg-error/10 text-error'}" class:correct={isCorrect}>
                                {#if isCorrect}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <polyline points="20 6 9 17 4 12"
                                        ></polyline>
                                    </svg>
                                    Correct
                                {:else}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    >
                                        <line x1="18" y1="6" x2="6" y2="18"
                                        ></line>
                                        <line x1="6" y1="6" x2="18" y2="18"
                                        ></line>
                                    </svg>
                                    Incorrect
                                {/if}
                            </span>
                        </div>

                        <p class="question-text text-base-content">{@html sanitizeHtml(question.questionText)}</p>

                        {#if question.questionImages?.length > 0}
                            <div class="question-images">
                                {#each question.questionImages as image}
                                    <img
                                        src={image.urlCachePWA || image.url}
                                        alt="Illustration de la question"
                                        class="question-image"
                                        on:error={(e) => { e.target.src = image.url; }}
                                    />
                                {/each}
                            </div>
                        {/if}

                        <div class="answer-review">
                            {#if question.questionType === 'image_hotspot'}
                                <div class="answer-section">
                                    <span class="badge badge-neutral gap-2">Zone correcte:</span>
                                    {#each question.options.filter(o => o.isCorrect) as option}
                                        <span>{@html sanitizeHtml(option.optionText)}</span>
                                    {/each}
                                </div>
                            {:else if question.questionType === 'match'}
                                <div class="answer-section">
                                    <span class="badge badge-neutral gap-2">Associations correctes:</span>
                                    <ul class="match-review-list">
                                        {#each question.options as option}
                                            {@const userMatchId = userAnswer[option.serverId]}
                                            {@const userMatchOption = userMatchId ? question.options.find(o => o.serverId === userMatchId) : null}
                                            {@const isMatchCorrect = userMatchId === option.serverId}
                                            <li class="match-review-item {isMatchCorrect ? 'text-success' : 'text-error'}">
                                                <span class="match-left">{@html sanitizeHtml(option.optionText)}</span>
                                                <span class="match-arrow">→</span>
                                                <span class="match-right">{@html sanitizeHtml(option.optionTextMatch)}</span>
                                                {#if !isMatchCorrect && userMatchOption}
                                                    <span class="match-user-answer text-error/70">
                                                        (votre réponse: {@html sanitizeHtml(userMatchOption.optionTextMatch)})
                                                    </span>
                                                {/if}
                                            </li>
                                        {/each}
                                    </ul>
                                </div>
                            {:else}
                                <div class="answer-section">
                                    <span class="badge badge-neutral gap-2"
                                        >Réponse{correctOptions.length > 1
                                            ? "s"
                                            : ""} correcte{correctOptions.length > 1
                                            ? "s"
                                            : ""}:</span
                                    >
                                    <ul>
                                        {#each correctOptions as option}
                                            <li>
                                                {@html sanitizeHtml(option.optionText)}
                                            </li>
                                        {/each}
                                    </ul>
                                </div>
                            {/if}
                            {#if question.explanation}
                                <div class="explanation bg-primary/10">
                                    <span class="badge badge-primary gap-2"
                                        >Explication:</span
                                    >
                                    <p class="text-base-content/70">{@html sanitizeHtml(question.explanation)}</p>
                                </div>
                            {/if}
                        </div>
                    </div>
                    {/if}
                {/each}
            </div>

            <div class="results-actions">
                <button class="primary-btn btn btn-primary" on:click={restartQuiz}>
                    Recommencer
                </button>
                <button class="secondary-btn btn btn-outline btn-primary" on:click={resetQuizAnswers}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <polyline points="23 4 23 10 17 10"></polyline>
                        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                    Réinitialiser
                </button>
                <button class="secondary-btn btn btn-outline btn-content" on:click={goBack}>Retour</button>
            </div>
        </div>
    {:else}
        <div class="quiz-container">
            {#if quiz.description}
                <p class="quiz-description bg-base-200 text-base-content/70">{quiz.description}</p>
            {/if}

            <div class="progress-bar bg-base-300">
                <div class="progress-fill bg-gradient-to-r from-primary to-secondary" style="width: {progress}%"></div>
            </div>
            <p class="progress-text text-base-content/70">
                Question {currentQuestionIndex + 1} sur {questions.length}
            </p>

            <div class="question-wrapper">
                <div class="question-card bg-primary/5 border-primary/30">
                    <div class="question-header bg-primary text-primary-content">
                        <span class="question-type">
                            {#if currentQuestion.questionType === "single_choice"}
                                Choix unique
                            {:else if currentQuestion.questionType === "multiple_choice"}
                                Choix multiples
                            {:else if currentQuestion.questionType === "match"}
                                Association
                            {:else if currentQuestion.questionType === "image_hotspot"}
                                Zone à trouver
                            {/if}
                        </span>
                    </div>
                    <div class="question-content">
                        <p class="question-text text-base-content">
                            {@html sanitizeHtml(currentQuestion.questionText)}
                        </p>

                        {#if currentQuestion.questionImages?.length > 0 && currentQuestion.questionType !== 'image_hotspot'}
                            <div class="question-images">
                                {#each currentQuestion.questionImages as image}
                                    <img
                                        src={image.urlCachePWA || image.url}
                                        alt="Illustration de la question"
                                        class="question-image"
                                        on:error={(e) => { e.target.src = image.url; }}
                                    />
                                {/each}
                            </div>
                        {/if}

                        {#if currentQuestion.questionType === "single_choice"}
                            <SingleChoiceOptions
                                options={currentQuestion.options}
                                selectedOptionIds={userAnswers[currentQuestionIndex]}
                                questionIndex={currentQuestionIndex}
                                on:select={(e) => handleAnswer(e.detail.optionId)}
                            />
                        {:else if currentQuestion.questionType === "multiple_choice"}
                            <MultipleChoiceOptions
                                options={currentQuestion.options}
                                selectedOptionIds={userAnswers[currentQuestionIndex]}
                                on:select={(e) => handleAnswer(e.detail.optionId)}
                            />
                        {:else if currentQuestion.questionType === "match"}
                            <MatchOptions
                                options={currentQuestion.options}
                                matches={userAnswers[currentQuestionIndex]}
                                on:match={(e) => handleAnswer(e.detail.optionId, e.detail.matchId)}
                            />
                        {:else if currentQuestion.questionType === "image_hotspot"}
                            <HotspotOptions
                                options={currentQuestion.options}
                                selectedOptionIds={userAnswers[currentQuestionIndex]}
                                questionImages={currentQuestion.questionImages}
                                on:select={(e) => handleAnswer(e.detail.optionId)}
                            />
                        {/if}
                    </div>
                </div>
            </div>

            <div class="nav-buttons">
                <button class="nav-btn btn btn-primary" on:click={prevQuestion} disabled={!hasPrev}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <path d="M15 18l-6-6 6-6" />
                    </svg>
                    Précédent
                </button>

                {#if hasNext}
                    <button class="nav-btn btn btn-primary" on:click={nextQuestion}>
                        Suivant
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                {:else}
                    <button class="nav-btn submit-btn btn btn-success" on:click={submitQuiz}>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Soumettre
                    </button>
                {/if}
            </div>
        </div>
    {/if}
</div>

<style>
    .quiz-page {
        min-height: 100vh;
        padding: 1.5rem;
        max-width: 900px;
        margin: 0 auto;
    }

    .page-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        margin-bottom: 2rem;
    }

    .back-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 1rem;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.95rem;
        transition: all 0.2s;
        width: fit-content;
    }

    .back-btn:hover {
    }

    h1 {
        margin: 0;
        font-size: 2rem;
    }

    .loading,
    .error,
    .empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        gap: 1rem;
    }

    .spinner {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        to {
            transform: rotate(360deg);
        }
    }

    .error,
    .empty {
    }

    .error button,
    .empty button {
        border: none;
        padding: 0.6rem 1.2rem;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.95rem;
    }

    .quiz-container {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .quiz-description {
        font-size: 1rem;
        line-height: 1.6;
        margin: 0;
        padding: 1rem;
        border-radius: 8px;
    }

    .progress-bar {
        width: 100%;
        height: 8px;
        border-radius: 4px;
        overflow: hidden;
    }

    .progress-fill {
        height: 100%;
        transition: width 0.3s ease;
    }

    .progress-text {
        text-align: center;
        font-size: 0.9rem;
        margin: 0;
    }

    .question-wrapper {
        min-height: 400px;
        display: flex;
        align-items: flex-start;
        justify-content: center;
    }

    .question-card {
        width: 100%;
        border-radius: 12px;
        border: 2px solid;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        overflow: hidden;
    }

    .question-header {
        padding: 0.8rem 1.2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .question-type {
        font-size: 0.85rem;
        font-weight: 600;
        text-transform: uppercase;
    }

    .question-content {
        padding: 2rem;
    }

    .question-text {
        font-size: 1.3rem;
        line-height: 1.6;
        margin: 0 0 2rem 0;
        font-weight: 500;
    }

    .question-images {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: center;
        margin-bottom: 1.5rem;
        max-width: 100%;
    }

    .question-image {
        max-width: 100%;
        max-height: 200px;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .nav-buttons {
        display: flex;
        gap: 1rem;
        justify-content: space-between;
    }

    .nav-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        border: none;
        padding: 0.8rem 1.5rem;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
    }

    .nav-btn:hover:not(:disabled) {
    }

    .nav-btn:disabled {
        cursor: not-allowed;
    }

    .nav-btn.submit-btn {
    }

    .nav-btn.submit-btn:hover {
    }

    .results {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2rem;
        padding: 2rem 0;
    }

    .results h2 {
        margin: 0;
        font-size: 2.5rem;
    }

    .score-display {
        display: flex;
        justify-content: center;
        margin: 1rem 0;
    }

    .score-circle {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 180px;
        height: 180px;
        border-radius: 50%;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    }

    .score-number {
        font-size: 3rem;
        font-weight: 700;
        line-height: 1;
    }

    .score-label {
        font-size: 1.2rem;
        opacity: 0.9;
        margin-top: 0.5rem;
    }

    .results-list {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }

    .results-list-header {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
    }

    .results-list h3 {
        font-size: 1.5rem;
        margin: 0;
        text-align: center;
    }

    .hide-correct-toggle {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        user-select: none;
    }

    .hide-correct-toggle span {
        font-size: 0.95rem;
    }

    .result-item {
        padding: 1.5rem;
        border-radius: 12px;
        border-width: 2px;
    }

    .result-item.correct {
    }

    .result-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
    }

    .question-number {
        font-size: 0.9rem;
        font-weight: 600;
        text-transform: uppercase;
    }

    .result-badge {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.4rem 0.8rem;
        border-radius: 6px;
        font-size: 0.85rem;
        font-weight: 600;
    }

    .result-badge.correct {
    }

    .question-text {
        font-size: 1.1rem;
        margin-bottom: 1rem;
        font-weight: 500;
    }

    .answer-review {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .answer-section {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }


    .answer-section ul {
        margin: 0;
        padding-left: 1.5rem;
    }

    .answer-section li {
        margin: 0.3rem 0;
        line-height: 1.5;
    }

    .match-review-list {
        list-style: none;
        padding-left: 0;
    }

    .match-review-item {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem 0;
        border-bottom: 1px solid oklch(var(--bc) / 0.1);
    }

    .match-review-item:last-child {
        border-bottom: none;
    }

    .match-left {
        font-weight: 500;
    }

    .match-arrow {
        opacity: 0.6;
    }

    .match-right {
        font-weight: 500;
    }

    .match-user-answer {
        font-size: 0.85rem;
        font-style: italic;
    }

    .explanation {
        padding: 1rem;
        border-radius: 8px;
        margin-top: 0.5rem;
    }


    .explanation p {
        margin: 0;
        line-height: 1.6;
    }

    .results-actions {
        display: flex;
        gap: 1rem;
        margin-top: 1rem;
        flex-wrap: wrap;
        justify-content: center;
    }

    .primary-btn,
    .secondary-btn {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.8rem 1.5rem;
        border-radius: 8px;
        font-size: 1rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
    }

    .primary-btn {
        border: none;
    }

    .primary-btn:hover {
    }

    .secondary-btn {
        border: 2px solid;
    }

    .secondary-btn:hover {
    }

    @media (max-width: 640px) {
        .quiz-page {
            padding: 1rem;
        }

        h1 {
            font-size: 1.5rem;
        }

        .question-content {
            padding: 1.5rem;
        }

        .question-text {
            font-size: 1.1rem;
        }

        .nav-buttons {
            flex-direction: column;
        }

        .nav-btn {
            width: 100%;
            justify-content: center;
        }

        .score-circle {
            width: 150px;
            height: 150px;
        }

        .score-number {
            font-size: 2.5rem;
        }

        .results-actions {
            flex-direction: column;
            width: 100%;
        }

        .results-actions button {
            width: 100%;
        }
    }
</style>
